import React, { useState, useEffect, useCallback } from 'react';
import { adminService, cohortService } from '../api/services';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Send, MessageSquare, Mail, Users, User, Briefcase,
  CheckCircle2, AlertCircle, Loader2, Info,
  History, RotateCcw, ChevronDown, ChevronUp, XCircle,
  Search, X as XIcon, UserCheck, FolderOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';

const AUDIENCES = [
  { value: 'ALL',     label: 'Everyone',     sub: 'All active clients and experts', icon: Users     },
  { value: 'CLIENTS', label: 'Clients Only', sub: 'All active clients',             icon: User      },
  { value: 'EXPERTS',     label: 'Experts Only',   sub: 'All approved experts',             icon: Briefcase  },
  { value: 'COHORT',      label: 'A Cohort',       sub: 'A saved group (e.g. by school)',   icon: FolderOpen },
  { value: 'INDIVIDUALS', label: 'Individuals',   sub: 'Search and pick specific people',   icon: UserCheck  },
];

const STATUS_ICON = {
  SENT:    <CheckCircle2 size={12} className="text-forest-600" />,
  FAILED:  <XCircle      size={12} className="text-rust-500"   />,
  SKIPPED: <span className="text-[10px] text-forest-300">—</span>,
};

// ── Compose Tab ───────────────────────────────────────────────
const ComposeTab = ({ onSent }) => {
  const [audience,     setAudience]     = useState('CLIENTS');
  const [subject,      setSubject]      = useState('');
  const [message,      setMessage]      = useState('');
  const [sendSms,      setSendSms]      = useState(true);
  const [sendEmail,    setSendEmail]    = useState(true);
  const [sending,      setSending]      = useState(false);
  const [result,       setResult]       = useState(null);
  const [audienceInfo, setAudienceInfo] = useState(null);
  // Individual search
  const [searchQ,      setSearchQ]      = useState('');
  const [searchResults,setSearchResults]= useState([]);
  const [searching,    setSearching]    = useState(false);
  const [selected,     setSelected]     = useState([]); // { id, firstName, lastName, email, phone }
  // Cohort picker
  const [cohorts,          setCohorts]          = useState([]);
  const [selectedCohortId, setSelectedCohortId] = useState('');

  useEffect(() => {
    if (audience === 'INDIVIDUALS' || audience === 'COHORT') return;
    adminService.getBroadcastAudience({ audience })
      .then(res => setAudienceInfo(res.data?.data ?? res.data))
      .catch(() => {});
  }, [audience]);

  useEffect(() => {
    if (audience !== 'COHORT' || cohorts.length) return;
    cohortService.getAll({ page: 1, limit: 100 })
      .then(res => {
        const body = res.data?.data ?? res.data;
        setCohorts(Array.isArray(body) ? body : (body?.cohorts ?? []));
      })
      .catch(() => {});
  }, [audience, cohorts.length]);

  // Search users as you type
  useEffect(() => {
    if (audience !== 'INDIVIDUALS' || searchQ.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminService.searchBroadcastUsers({ q: searchQ });
        const d   = res.data?.data ?? res.data;
        setSearchResults((d?.users ?? []).filter(u => !selected.find(s => s.id === u.id)));
      } catch {}
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQ, audience, selected]);

  const addRecipient = (user) => {
    setSelected(prev => [...prev, user]);
    setSearchQ(''); setSearchResults([]);
  };
  const removeRecipient = (id) => setSelected(prev => prev.filter(u => u.id !== id));

  const selectedCohort = cohorts.find(c => c.id === selectedCohortId);

  const handleSend = async () => {
    if (!message.trim()) { toast.error('Please enter a message'); return; }
    if (!sendSms && !sendEmail) { toast.error('Select at least one channel'); return; }
    if (audience === 'INDIVIDUALS' && !selected.length) { toast.error('Add at least one recipient'); return; }
    if (audience === 'COHORT' && !selectedCohortId) { toast.error('Pick a cohort'); return; }
    const recipientCount = audience === 'INDIVIDUALS' ? selected.length
      : audience === 'COHORT' ? (selectedCohort?._count?.members ?? '?')
      : audienceInfo?.total ?? '?';
    if (!window.confirm(`Send to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}?`)) return;

    setSending(true); setResult(null);
    try {
      let res;
      if (audience === 'INDIVIDUALS') {
        res = await adminService.sendToIndividuals({
          userIds: selected.map(u => u.id), subject, message, sendSms, sendEmail,
        });
      } else if (audience === 'COHORT') {
        res = await adminService.sendToCohort({
          cohortId: selectedCohortId, subject, message, sendSms, sendEmail,
        });
      } else {
        res = await adminService.sendBroadcast({ audience, subject, message, sendSms, sendEmail });
      }
      const d = res.data?.data ?? res.data;
      setResult({ success: true, ...d });
      toast.success('Message sent!');
      setMessage(''); setSubject(''); setSelected([]);
      onSent();
    } catch (err) {
      const msg = err.response?.data?.message || 'Send failed';
      setResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      {result && (
        <div className={`flex items-start gap-3 rounded-2xl px-5 py-4 ${result.success ? 'bg-forest-50 border border-forest-200' : 'bg-rust-50 border border-rust-200'}`}>
          {result.success ? <CheckCircle2 size={18} className="text-forest-600 mt-0.5" /> : <AlertCircle size={18} className="text-rust-500 mt-0.5" />}
          <div className="text-sm">
            <p className={`font-semibold ${result.success ? 'text-forest-800' : 'text-rust-700'}`}>{result.success ? 'Broadcast Sent!' : 'Failed'}</p>
            <p className={result.success ? 'text-forest-600' : 'text-rust-600'}>{result.message}</p>
            {result.success && (
              <div className="flex gap-4 mt-2 text-xs text-forest-500">
                <span>📨 {result.smsCount} SMS</span>
                <span>📧 {result.emailCount} Emails</span>
                {result.failed > 0 && <span className="text-rust-500 font-medium">⚠ {result.failed} failed — check History tab to retry</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audience */}
      <div>
        <label className="block text-sm font-semibold text-forest-800 mb-3">Audience</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {AUDIENCES.map(a => {
            const Icon = a.icon;
            return (
              <button key={a.value} onClick={() => setAudience(a.value)}
                className={`flex flex-col items-start p-4 rounded-xl border-2 transition-all text-left ${audience === a.value ? 'border-forest-700 bg-forest-50' : 'border-cream-200 bg-white hover:border-forest-300'}`}>
                <Icon size={16} className={audience === a.value ? 'text-forest-700' : 'text-forest-400'} />
                <p className={`text-sm font-semibold mt-2 ${audience === a.value ? 'text-forest-900' : 'text-forest-600'}`}>{a.label}</p>
                <p className="text-xs text-forest-400">{a.sub}</p>
              </button>
            );
          })}
        </div>
        {audienceInfo && audience !== 'INDIVIDUALS' && audience !== 'COHORT' && (
          <div className="mt-2 flex items-center gap-2 text-xs text-forest-500">
            <Info size={12} />
            <span className="font-semibold text-forest-700">{audienceInfo.total}</span> recipients
            {sendSms   && <> · <span className="font-semibold text-forest-700">{audienceInfo.withPhone}</span> with phone</>}
            {sendEmail && <> · <span className="font-semibold text-forest-700">{audienceInfo.withEmail}</span> with email</>}
          </div>
        )}

        {/* Cohort picker */}
        {audience === 'COHORT' && (
          <div className="mt-3 space-y-2">
            {cohorts.length === 0 ? (
              <p className="text-xs text-forest-400 italic">
                No cohorts yet — create one on the <a href="/admin/cohorts" className="underline text-forest-600">Cohorts page</a> first.
              </p>
            ) : (
              <>
                <select value={selectedCohortId} onChange={e => setSelectedCohortId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-800 focus:outline-none focus:border-forest-500 bg-white">
                  <option value="">Select a cohort...</option>
                  {cohorts.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c._count?.members ?? 0})</option>
                  ))}
                </select>
                {selectedCohort && (
                  <div className="flex items-center gap-2 text-xs text-forest-500">
                    <Info size={12} />
                    <span className="font-semibold text-forest-700">{selectedCohort._count?.members ?? 0}</span> recipients in "{selectedCohort.name}"
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Individual search */}
        {audience === 'INDIVIDUALS' && (
          <div className="mt-3 space-y-3">
            {/* Search box */}
            <div className="relative">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
              <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
              {searching && <Loader2 size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-forest-400 animate-spin" />}
            </div>

            {/* Search results dropdown */}
            {searchResults.length > 0 && (
              <div className="bg-white border border-cream-200 rounded-xl shadow-lg overflow-hidden">
                {searchResults.map(u => (
                  <button key={u.id} onClick={() => addRecipient(u)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream-50 transition-colors text-left border-b border-cream-50 last:border-0">
                    <div className="w-7 h-7 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 text-xs font-bold flex-shrink-0">
                      {u.firstName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-forest-900">{u.firstName} {u.lastName}</p>
                      <p className="text-xs text-forest-400 truncate">{u.email} · {u.role}</p>
                    </div>
                    <span className="text-xs text-forest-500 border border-forest-200 px-2 py-0.5 rounded-full">Add</span>
                  </button>
                ))}
              </div>
            )}

            {/* Selected recipients */}
            {selected.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-forest-600 mb-2">{selected.length} recipient{selected.length !== 1 ? 's' : ''} selected</p>
                <div className="flex flex-wrap gap-2">
                  {selected.map(u => (
                    <div key={u.id} className="flex items-center gap-1.5 bg-forest-50 border border-forest-200 text-forest-700 text-xs px-3 py-1.5 rounded-full">
                      <span>{u.firstName} {u.lastName}</span>
                      <button onClick={() => removeRecipient(u.id)} className="text-forest-400 hover:text-rust-500">
                        <XIcon size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected.length === 0 && searchQ.length < 2 && (
              <p className="text-xs text-forest-400 italic">Type at least 2 characters to search for recipients.</p>
            )}
          </div>
        )}
      </div>

      {/* Channels */}
      <div>
        <label className="block text-sm font-semibold text-forest-800 mb-3">Channels</label>
        <div className="flex gap-4">
          {[
            { label: 'SMS',   icon: MessageSquare, val: sendSms,   set: setSendSms   },
            { label: 'Email', icon: Mail,          val: sendEmail, set: setSendEmail },
          ].map(({ label, icon: Icon, val, set }) => (
            <label key={label} className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${val ? 'border-forest-700 bg-forest-50' : 'border-cream-200 bg-white'}`}>
              <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} className="w-4 h-4 accent-forest-700" />
              <Icon size={15} className={val ? 'text-forest-700' : 'text-forest-400'} />
              <span className="text-sm font-medium text-forest-700">{label}</span>
            </label>
          ))}
        </div>
      </div>

      {sendEmail && (
        <div>
          <label className="block text-sm font-semibold text-forest-800 mb-1.5">Email Subject</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="e.g. Important update from TourentHub"
            className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-semibold text-forest-800">Message</label>
          <span className="text-xs text-forest-400">{message.length} chars</span>
        </div>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={6}
          placeholder={`Dear {firstName},\n\nYour message here...\n\n— TourentHub Team`}
          className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 resize-none" />
        <p className="text-xs text-forest-400 mt-1">Use <code className="bg-cream-100 px-1 rounded">{'{firstName}'}</code> to personalise.</p>
      </div>

      <button onClick={handleSend} disabled={sending || !message.trim() || (!sendSms && !sendEmail)}
        className="flex items-center gap-2 text-sm font-medium bg-forest-800 text-cream-50 px-6 py-3 rounded-full hover:bg-forest-700 transition-colors disabled:opacity-50">
        {sending ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : <><Send size={15} /> Send Broadcast</>}
      </button>
    </div>
  );
};

// ── History Tab ───────────────────────────────────────────────
const HistoryTab = ({ refresh }) => {
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [detail,    setDetail]    = useState({});
  const [retrying,  setRetrying]  = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await adminService.getBroadcastLogs({ limit: 50 });
      const body = res.data;
      setLogs(Array.isArray(body.data) ? body.data : []);
    } catch { toast.error('Could not load broadcast history'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs, refresh]);

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        const res = await adminService.getBroadcastDetail(id);
        const d   = res.data?.data ?? res.data;
        setDetail(prev => ({ ...prev, [id]: d?.log ?? d }));
      } catch {}
    }
  };

  const handleRetry = async (broadcastId, channel) => {
    if (!window.confirm(`Retry all failed ${channel || 'SMS + Email'} for this broadcast?`)) return;
    setRetrying(broadcastId + (channel || ''));
    try {
      const res = await adminService.retryBroadcast({ broadcastId, channel });
      const d   = res.data?.data ?? res.data;
      toast.success(d.message || 'Retry sent');
      fetchLogs();
      setDetail(prev => ({ ...prev, [broadcastId]: undefined })); // clear cached detail
    } catch (err) {
      toast.error(err.response?.data?.message || 'Retry failed');
    } finally { setRetrying(null); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-forest-500 text-sm py-8 justify-center">
      <Loader2 size={16} className="animate-spin" /> Loading history...
    </div>
  );

  if (!logs.length) return (
    <div className="text-center py-16">
      <History size={32} className="text-forest-200 mx-auto mb-3" />
      <p className="font-serif text-xl text-forest-600">No broadcasts yet</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {logs.map(log => {
        const isOpen = expanded === log.id;
        const d      = detail[log.id];
        const failedRecipients = d?.recipients?.filter(r => r.smsStatus === 'FAILED' || r.emailStatus === 'FAILED') ?? [];
        const smsFailedCount   = d?.recipients?.filter(r => r.smsStatus   === 'FAILED').length ?? 0;
        const emailFailedCount = d?.recipients?.filter(r => r.emailStatus === 'FAILED').length ?? 0;

        return (
          <div key={log.id} className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
            {/* Header row */}
            <button onClick={() => toggleExpand(log.id)}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-cream-50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-forest-900 truncate">{log.subject || 'No subject'}</p>
                  <span className="text-[10px] bg-cream-100 text-forest-500 px-2 py-0.5 rounded-full">{log.audience}</span>
                  {log.sendSms   && <span className="text-[10px] bg-blue-50  text-blue-600  px-2 py-0.5 rounded-full">SMS</span>}
                  {log.sendEmail && <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">Email</span>}
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-forest-400">
                  <span>{new Date(log.createdAt).toLocaleString('en-GH')}</span>
                  <span className="text-forest-600">✉ {log.emailCount} sent</span>
                  <span className="text-forest-600">📨 {log.smsCount} SMS</span>
                  {log.failedCount > 0 && <span className="text-rust-500 font-medium">⚠ {log.failedCount} failed</span>}
                </div>
              </div>
              {isOpen ? <ChevronUp size={16} className="text-forest-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-forest-400 flex-shrink-0" />}
            </button>

            {/* Expanded detail */}
            {isOpen && (
              <div className="border-t border-cream-100 px-5 py-4 space-y-4">
                {/* Message preview */}
                <div className="bg-cream-50 rounded-xl p-3 text-sm text-forest-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                  {log.message}
                </div>

                {/* Retry buttons */}
                {log.failedCount > 0 && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-xs text-rust-600 font-medium">{log.failedCount} failed deliveries:</p>
                    {smsFailedCount > 0 && (
                      <button onClick={() => handleRetry(log.id, 'SMS')}
                        disabled={retrying === log.id + 'SMS'}
                        className="flex items-center gap-1.5 text-xs font-medium border border-blue-200 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
                        {retrying === log.id + 'SMS' ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Retry {smsFailedCount} SMS
                      </button>
                    )}
                    {emailFailedCount > 0 && (
                      <button onClick={() => handleRetry(log.id, 'EMAIL')}
                        disabled={retrying === log.id + 'EMAIL'}
                        className="flex items-center gap-1.5 text-xs font-medium border border-amber-200 text-amber-600 hover:bg-amber-50 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
                        {retrying === log.id + 'EMAIL' ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Retry {emailFailedCount} Emails
                      </button>
                    )}
                    <button onClick={() => handleRetry(log.id, null)}
                      disabled={!!retrying}
                      className="flex items-center gap-1.5 text-xs font-medium border border-rust-200 text-rust-600 hover:bg-rust-50 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50">
                      {retrying === log.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                      Retry All Failed
                    </button>
                  </div>
                )}

                {/* Recipients table */}
                {d?.recipients ? (
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-cream-50">
                        <tr className="text-forest-400 uppercase tracking-wider text-[10px]">
                          <th className="text-left py-2 px-2">Name</th>
                          <th className="text-left py-2 px-2">SMS</th>
                          <th className="text-left py-2 px-2">Email</th>
                          <th className="text-left py-2 px-2">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-cream-50">
                        {d.recipients.map(r => (
                          <tr key={r.id} className={`${(r.smsStatus === 'FAILED' || r.emailStatus === 'FAILED') ? 'bg-rust-50/50' : ''}`}>
                            <td className="py-2 px-2 font-medium text-forest-700">{r.firstName}</td>
                            <td className="py-2 px-2">{STATUS_ICON[r.smsStatus]}</td>
                            <td className="py-2 px-2">{STATUS_ICON[r.emailStatus]}</td>
                            <td className="py-2 px-2 text-rust-400 truncate max-w-[200px]">
                              {r.smsError || r.emailError || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-forest-400">
                    <Loader2 size={12} className="animate-spin" /> Loading recipients...
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const AdminBroadcastPage = () => {
  const [tab,     setTab]     = useState('compose');
  const [refresh, setRefresh] = useState(0);

  return (
    <DashboardLayout title="Broadcast">
      <div className="max-w-3xl space-y-5">
        <p className="text-sm text-forest-500">
          Send bulk SMS and email to clients or experts. Use <code className="bg-cream-100 px-1 rounded text-xs">{'{firstName}'}</code> to personalise.
        </p>

        {/* Tabs */}
        <div className="flex gap-1 bg-cream-100 p-1 rounded-xl w-fit">
          {[
            { id: 'compose', label: 'Compose',    icon: Send    },
            { id: 'history', label: 'History & Retry', icon: History },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  tab === t.id ? 'bg-white text-forest-900 shadow-sm' : 'text-forest-500 hover:text-forest-700'
                }`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'compose' && <ComposeTab onSent={() => setRefresh(r => r + 1)} />}
        {tab === 'history' && <HistoryTab refresh={refresh} />}
      </div>
    </DashboardLayout>
  );
};

export default AdminBroadcastPage;